import { AxiosResponse } from "axios";

import { axiosInstance } from "./common";

export enum AssetClassName {
  FINANCIAL = "FINANCIAL",
  REAL_ESTATE = "REAL_ESTATE",
  LAND_PARCEL = "LAND_PARCEL",
  MOVABLE = "MOVABLE",
  WORKING_CAPITAL = "WORKING_CAPITAL",
}

export enum AssetType {
  FIXED_DEPOSIT = "FIXED_DEPOSIT",
  MARKETABLE_SECURITY = "MARKETABLE_SECURITY",
  GOVERNMENT_DEBT_INSTRUMENT = "GOVERNMENT_DEBT_INSTRUMENT",
  SMALL_SAVINGS_SCHEME = "SMALL_SAVINGS_SCHEME",
  INSURANCE_SECURITY_ASSET = "INSURANCE_SECURITY_ASSET",

  RESIDENTIAL = "RESIDENTIAL",
  COMMERCIAL = "COMMERCIAL",
  INDUSTRIAL = "INDUSTRIAL",

  AGRICULTURAL = "AGRICULTURAL",
  NON_AGRICULTURAL = "NON_AGRICULTURAL",
  INDUSTRIAL_ALLOTMENT = "INDUSTRIAL_ALLOTMENT",
  RESIDENTIAL_PLOT = "RESIDENTIAL_PLOT",
  COMMERCIAL_PLOT = "COMMERCIAL_PLOT",

  PLANT_AND_MACHINERY = "PLANT_AND_MACHINERY",
  COMMERCIAL_EQUIPMENT = "COMMERCIAL_EQUIPMENT",
  VEHICLE = "VEHICLE",
  PRECIOUS_METAL = "PRECIOUS_METAL",

  STOCK_AND_INVENTORY = "STOCK_AND_INVENTORY",
  TRADE_RECEIVABLE = "TRADE_RECEIVABLE",
  CORPORATE_GUARANTEE = "CORPORATE_GUARANTEE",
}

export const ASSET_CLASS_LABELS: Record<AssetClassName, string> = {
  [AssetClassName.FINANCIAL]: "Financial Asset",
  [AssetClassName.REAL_ESTATE]: "Real Estate",
  [AssetClassName.LAND_PARCEL]: "Land Parcel",
  [AssetClassName.MOVABLE]: "Movable Asset",
  [AssetClassName.WORKING_CAPITAL]: "Working Capital Asset",
};

export const ASSET_TYPE_LABELS: Record<AssetType, string> = {
  [AssetType.FIXED_DEPOSIT]: "Fixed Deposit",
  [AssetType.MARKETABLE_SECURITY]: "Marketable Security",
  [AssetType.GOVERNMENT_DEBT_INSTRUMENT]: "Government Debt Instrument",
  [AssetType.SMALL_SAVINGS_SCHEME]: "Small Savings Scheme",
  [AssetType.INSURANCE_SECURITY_ASSET]: "Insurance Security Asset",

  [AssetType.RESIDENTIAL]: "Residential",
  [AssetType.COMMERCIAL]: "Commercial",
  [AssetType.INDUSTRIAL]: "Industrial",

  [AssetType.AGRICULTURAL]: "Agricultural",
  [AssetType.NON_AGRICULTURAL]: "Non Agricultural",
  [AssetType.INDUSTRIAL_ALLOTMENT]: "Industrial Allotment",
  [AssetType.RESIDENTIAL_PLOT]: "Residential Plot",
  [AssetType.COMMERCIAL_PLOT]: "Commercial Plot",

  [AssetType.PLANT_AND_MACHINERY]: "Plant And Machinery",
  [AssetType.COMMERCIAL_EQUIPMENT]: "Commercial Equipment",
  [AssetType.VEHICLE]: "Vehicle",
  [AssetType.PRECIOUS_METAL]: "Precious Metal",

  [AssetType.STOCK_AND_INVENTORY]: "Stock And Inventory",
  [AssetType.TRADE_RECEIVABLE]: "Trade Receivable",
  [AssetType.CORPORATE_GUARANTEE]: "Corporate Guarantee",
};

export const FINANCIAL_ASSET_TYPES: AssetType[] = [
  AssetType.FIXED_DEPOSIT,
  AssetType.MARKETABLE_SECURITY,
  AssetType.GOVERNMENT_DEBT_INSTRUMENT,
  AssetType.SMALL_SAVINGS_SCHEME,
  AssetType.INSURANCE_SECURITY_ASSET,
];

export const REAL_ESTATE_TYPES: AssetType[] = [
  AssetType.RESIDENTIAL,
  AssetType.COMMERCIAL,
  AssetType.INDUSTRIAL,
];

export const LAND_PARCEL_TYPES: AssetType[] = [
  AssetType.AGRICULTURAL,
  AssetType.NON_AGRICULTURAL,
  AssetType.INDUSTRIAL_ALLOTMENT,
  AssetType.RESIDENTIAL_PLOT,
  AssetType.COMMERCIAL_PLOT,
];

export const MOVABLE_ASSET_TYPES: AssetType[] = [
  AssetType.PLANT_AND_MACHINERY,
  AssetType.COMMERCIAL_EQUIPMENT,
  AssetType.VEHICLE,
  AssetType.PRECIOUS_METAL,
];

export const WORKING_CAPITAL_ASSET_TYPES: AssetType[] = [
  AssetType.STOCK_AND_INVENTORY,
  AssetType.TRADE_RECEIVABLE,
  AssetType.CORPORATE_GUARANTEE,
];

export interface AssetFields {
  ext_asset_id: string;
  asset_class: AssetClassName;
  asset_type: AssetType;
  asset_name?: string;
}

export interface Asset extends AssetFields {
  asset_id: string;
}

export interface UpdateAssetRequest {
  ext_asset_id?: string;
  asset_class?: AssetClassName;
  asset_type?: AssetType;
  asset_name?: string;
}

export interface GetAssetResponse {
  assets: Asset[];
}

export interface MessageResponse {
  message: string;
}

export interface GetAssetsParams {
  case_id: string;
}

export interface CreateAssetParams {
  case_id: string;
  request: AssetFields;
}

export interface UpdateAssetParams {
  case_id: string;
  asset_id: string;
  request: UpdateAssetRequest;
}

const AssetsAPI = {
  getAssets: async (
    params: GetAssetsParams,
  ) => {
    const response: AxiosResponse<GetAssetResponse> =
      await axiosInstance.get(
        `/v1/cases/${params.case_id}/assets`,
      );

    return response.data;
  },

  createAsset: async (
    params: CreateAssetParams,
  ) => {
    const response: AxiosResponse<Asset> =
      await axiosInstance.post(
        `/v1/cases/${params.case_id}/assets`,
        params.request,
      );

    return response.data;
  },

  updateAsset: async (
    params: UpdateAssetParams,
  ) => {
    const response: AxiosResponse<MessageResponse> =
      await axiosInstance.patch(
        `/v1/cases/${params.case_id}/assets/${params.asset_id}`,
        params.request,
      );

    return response.data;
  },
};

export default AssetsAPI;